/**
 * Scenario Planner Engine (Phase 3 — Steps 3.1, 3.3).
 *
 * Future absence simulation and back-calculation from target grade.
 */
import { AbsenceType } from '@abs/shared-types';
import { computeEffectiveAbsences } from './budgetCalculator';
import { computeAttendancePercentage, computeMaxAbsences } from './budgetCalculator';
import { resolveActiveTier, resolveLetterGrade, computeTotalDeduction } from './penaltyResolver';
import { classifyRisk } from './riskAssessor';
/**
 * Simulate the effect of N additional unexcused absences.
 *
 * @param course - Current course
 * @param policy - Attendance policy
 * @param entries - Current absence entries
 * @param totalSessions - Total session count
 * @param additionalAbsences - Number of future unexcused absences to simulate
 * @param baseGrade - Current base grade (null = no grade projection)
 */
export function simulateFutureAbsences(course, policy, entries, totalSessions, additionalAbsences, baseGrade) {
    // Create synthetic entries (unexcused absences on future dates)
    const syntheticEntries = [
        ...entries,
        ...Array.from({ length: additionalAbsences }, (_, i) => ({
            id: `sim-${i}`,
            courseId: course.id,
            date: `2099-01-${(i + 1).toString().padStart(2, '0')}`,
            type: AbsenceType.Unexcused,
            reason: null,
            arrivalTime: null,
            departureTime: null,
            note: 'Simulated',
            isEdited: false,
            createdAt: '2099-01-01T00:00:00Z',
            updatedAt: '2099-01-01T00:00:00Z',
        })),
    ];
    const effectiveAbsences = computeEffectiveAbsences(syntheticEntries, policy);
    const maxAbsences = computeMaxAbsences(policy, totalSessions);
    const remaining = Math.max(0, maxAbsences - effectiveAbsences.totalPenaltyAbsences);
    const projectedPercentage = computeAttendancePercentage(totalSessions, effectiveAbsences.totalPenaltyAbsences);
    const { activeTier, nextTier } = resolveActiveTier(policy.tiers, effectiveAbsences.totalPenaltyAbsences);
    // Current active tier for comparison
    const currentEffective = computeEffectiveAbsences(entries, policy);
    const { activeTier: currentTier } = resolveActiveTier(policy.tiers, currentEffective.totalPenaltyAbsences);
    // Which new tier is triggered
    const tierTriggered = activeTier?.id !== currentTier?.id ? activeTier : null;
    const isAutoFail = remaining === 0 || projectedPercentage <= policy.attendanceFloor;
    const riskLevel = classifyRisk(remaining, maxAbsences, projectedPercentage, policy.attendanceFloor, activeTier, nextTier, effectiveAbsences.totalPenaltyAbsences);
    // Grade projection
    let projectedGrade = null;
    if (baseGrade !== null) {
        const deduction = computeTotalDeduction(policy, effectiveAbsences);
        const gradePoints = Math.max(0, baseGrade - deduction);
        projectedGrade = resolveLetterGrade(gradePoints, course.gradingScale);
    }
    return {
        projectedPercentage,
        projectedGrade,
        projectedRiskLevel: riskLevel,
        tierTriggered,
        isAutoFail,
        additionalAbsences,
    };
}
/**
 * Back-calculate how many more absences are affordable
 * to maintain a target letter grade.
 *
 * @returns Number of additional absences before dropping below target.
 *          Returns Infinity if target is achievable regardless.
 *          Returns 0 if already below target.
 */
export function backCalculateMaxAbsences(targetGrade, gradingScale, baseGrade, policy, entries, totalSessions) {
    // Find target grade threshold
    const targetEntry = gradingScale.find((g) => g.letter === targetGrade);
    if (!targetEntry)
        return 0;
    const targetPoints = targetEntry.minPercentage;
    // Check current grade
    const currentEffective = computeEffectiveAbsences(entries, policy);
    const currentDeduction = computeTotalDeduction(policy, currentEffective);
    const currentGrade = baseGrade - currentDeduction;
    if (currentGrade < targetPoints)
        return 0; // Already below target
    // Binary search for maximum affordable absences
    let lo = 0;
    let hi = totalSessions; // Worst case: miss every session
    while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        const sim = simulateFutureAbsences({ id: 'sim', name: 'sim', code: 'sim', instructor: '', termId: '', policyId: '',
            meetingPatterns: [], sessionOverrides: [], totalSessions, creditHours: 3,
            startDate: '', endDate: '', baseGrade, gradingScale,
            createdAt: '2099-01-01T00:00:00Z', updatedAt: '2099-01-01T00:00:00Z' }, policy, entries, totalSessions, mid, baseGrade);
        if (sim.projectedGrade) {
            const simGradeEntry = gradingScale.find((g) => g.letter === sim.projectedGrade);
            const simPoints = simGradeEntry?.minPercentage ?? 0;
            if (simPoints >= targetPoints) {
                lo = mid;
            }
            else {
                hi = mid - 1;
            }
        }
        else {
            hi = mid - 1;
        }
    }
    return lo;
}
/**
 * Back-calculate how many more sessions can be missed while staying at or
 * above a target attendance percentage.
 *
 * @param totalSessions - Total scheduled sessions
 * @param usedAbsences - Penalty absences already used
 * @param targetAttendancePct - Target attendance % (0–100), e.g. 90
 * @returns Additional sessions that can be missed; never negative.
 */
export function backCalculateForAttendanceTarget(totalSessions, usedAbsences, targetAttendancePct) {
    if (totalSessions <= 0)
        return 0;
    // Integer-friendly form of floor(totalSessions * (1 − targetAttendancePct/100)).
    // floor(total*(1−t/100)) == total − ceil(total*t/100), and avoids float error.
    const minAttended = Math.ceil((totalSessions * targetAttendancePct) / 100);
    const allowedTotalMisses = Math.max(0, totalSessions - minAttended);
    return Math.max(0, allowedTotalMisses - usedAbsences);
}
//# sourceMappingURL=scenarioPlanner.js.map